import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrderDetails from "../OrderDetails";
import { HelmetProvider } from 'react-helmet-async';
import Footer from "../../../../components/Footer";
import Loader from "../../../../components/Loader/Loader";
import { postPassengers } from "../../../../services/experienceOrderService";
import MetaTags from "../../../../components/MetaTags";
import PassengersDetails, { type Passenger } from "../../../../components/Checkout/Passengers/PassengersDetails";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import Header from "../../../../components/Header";
import useExperienceData from "../../../../hooks/useExperienceData";
import { useCheckoutContext } from "../../../../context/experienceCheckoutContext";

const Passengers = () => {
    const currentStep = 0; // this is an index representing the breadcrumb step
    const [loading, setLoading] = useState(true);
    const [isFormValid, setIsFormValid] = useState(false);
    const [passengers, setPassengers] = useState<Passenger[]>([]);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    // Get order from checkout context
    const { order, loading: orderLoading, fetchOrder } = useCheckoutContext();
    const { experience, promoter } = useExperienceData();

    const numPassengersInParams = Number(searchParams.get("num_passengers"))


    // Initialize passengers when order data is loaded
    useEffect(() => {
        if (order) {
            if (order.passengers && order.passengers.length > 0) {
                setPassengers(
                    order.passengers.map((passenger) => {
                        return {
                            id: passenger.public_id,
                            fullName: passenger.full_name,
                            phone: passenger.phone,
                            email: passenger.email,
                            dob: passenger.date_of_birth,
                            idType: passenger.id_type,
                            idNumber: passenger.id_number,
                            nationality: passenger.nationality,
                            errors: {},
                        };
                    })
                );
            } else {

                const initialPassengers = Array(numPassengersInParams).fill({
                    fullName: "",
                    phone: "",
                    email: "",
                    nationality: "",
                    dob: undefined,
                    idType: "",
                    idNumber: "",
                    errors: {},
                });

                setPassengers(initialPassengers);
            }
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    useEffect(() => {
        const formValid = passengers.every(
            (passenger) =>
                passenger.fullName &&
                passenger.phone &&
                passenger.email &&
                passenger.nationality &&
                passenger.dob &&
                passenger.idType &&
                passenger.idNumber &&
                !passenger.errors.fullName &&
                !passenger.errors.phone &&
                !passenger.errors.email &&
                !passenger.errors.nationality &&
                !passenger.errors.dob &&
                !passenger.errors.idType &&
                !passenger.errors.idNumber
        );
        setIsFormValid(formValid);
    }, [passengers]);

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    const handlePassengerChange = (index: number, field: string, value: string | Date) => {
        const newPassengers = [...passengers];
        newPassengers[index] = { ...newPassengers[index], [field]: value };
        setPassengers(newPassengers);
    };

    const handlePassengerBlur = (index: number, field: string, validateFn: (value: any) => string | undefined) => {
        const newPassengers = [...passengers];
        const passenger = newPassengers[index];
        const error = validateFn(passenger[field as keyof typeof passenger]);
        newPassengers[index] = {
            ...newPassengers[index],
            errors: { ...newPassengers[index].errors, [field]: error },
        };
        setPassengers(newPassengers);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isFormValid && order) {
            setLoading(true);
            try {
                const passengersData = passengers.map((passenger) => {
                    const passengerItem: {
                        full_name: string;
                        phone: string;
                        email: string;
                        date_of_birth: Date | undefined;
                        id_type: string;
                        id_number: string;
                        nationality: string;
                        public_id?: string;
                    } = {
                        full_name: passenger.fullName || "",
                        phone: String(passenger.phone) || "",
                        email: passenger.email || "",
                        date_of_birth: passenger.dob,
                        id_type: passenger.idType || "",
                        id_number: passenger.idNumber || "",
                        nationality: passenger.nationality || ""
                    };

                    if (passenger.id) {
                        passengerItem.public_id = passenger.id;
                    }

                    return passengerItem;
                });

                await postPassengers({ passengers: passengersData }, order.booking.public_id);
                await fetchOrder();
                navigate(`/experiences/${experience?.aliasUrl}/checkout/billing`);
            } catch (e) {
                console.log(e);
                window.alert("There was a problem with the server!");
                setLoading(false);
            }
        }
    };

    if (loading || orderLoading || !experience || !promoter) return <Loader />;

    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.search

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter?.fullname || ""}
                icon={promoter?.logoURL || ""}
                url={currentUrl}
                font={experience.font ? experience.font : null}
                primaryColor={experience.colors.primary_color ? experience.colors.primary_color : null}
                secondaryColor={experience.colors.secondary_color ? experience.colors.secondary_color : null}
            />
            <Header loading={false} promoter={promoter} stock={Infinity} error={null}></Header>
            <div className="passengers__container">
                <Breadcrumbs currentStep={currentStep} orderStatus={null} noGoBack={true}></Breadcrumbs>
                <div className="main-content">
                    <PassengersDetails
                        passengers={passengers}
                        onChange={handlePassengerChange}
                        onBlur={handlePassengerBlur}
                    />
                    <OrderDetails
                        handleCheckboxChange={() => { alert("Not implemented") }}
                        handleSubmitNextStep={handleSubmit}
                        isFormValid={true}
                        nextStepLabel={"Siguiente paso"}
                    />
                </div>
                {!isSmallScreen && (
                    <div className="checkout-actions">
                        <button type="button" onClick={handleSubmit} disabled={!isFormValid}>
                            Siguiente paso
                        </button>
                    </div>
                )}
            </div>
            <Footer loading={false} promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default Passengers;
